import * as yup from 'yup';

import { CHAIN_STAKE_CONFIG, ChainKey } from '../config/chainStakeConfig';

// ========== TYPES ==========

export interface StakeValidationResult {
  isValid: boolean;
  errorKey: string | null;
}

export interface StakeLimits {
  min: number;
  max: number;
}

// ========== ERROR KEYS (i18n) ==========

export const STAKE_ERROR_KEYS = {
  BELOW_MINIMUM: 'stake.error.belowMinimum',
  ABOVE_MAXIMUM: 'stake.error.aboveMaximum',
  INVALID_TYPE: 'stake.error.invalidType',
  MUST_BE_INTEGER: 'stake.error.mustBeInteger',
  NEGATIVE: 'stake.error.negative',
} as const;

export type StakeErrorKey = (typeof STAKE_ERROR_KEYS)[keyof typeof STAKE_ERROR_KEYS];

// ========== CONSTANTS ==========

/**
 * Default POL limits, kept for backward compatibility with existing callers
 * and tests that don't yet pass a chain-aware limits object.
 */
export const STAKE_LIMITS: StakeLimits = {
  min: CHAIN_STAKE_CONFIG.polygon.customMin,
  max: CHAIN_STAKE_CONFIG.polygon.customMax,
};

// Aliases preserved for legacy imports (`STAKE_LIMITS.MIN` / `.MAX`).
Object.defineProperty(STAKE_LIMITS, 'MIN', {
  get: () => CHAIN_STAKE_CONFIG.polygon.customMin,
  enumerable: true,
});
Object.defineProperty(STAKE_LIMITS, 'MAX', {
  get: () => CHAIN_STAKE_CONFIG.polygon.customMax,
  enumerable: true,
});

export const limitsForChain = (chain: ChainKey): StakeLimits => ({
  min: CHAIN_STAKE_CONFIG[chain].customMin,
  max: CHAIN_STAKE_CONFIG[chain].customMax,
});

// ========== VALIDATION FUNCTION ==========

/**
 * Synchronous validation of a stake amount against chain-specific limits.
 *
 * @param amount - Value to validate
 * @param limits - Optional override; defaults to POL limits for backward compat
 */
export const validateStakeAmount = (
  amount: unknown,
  limits: StakeLimits = STAKE_LIMITS,
): StakeValidationResult => {
  if (
    amount === null ||
    amount === undefined ||
    typeof amount !== 'number' ||
    !Number.isFinite(amount)
  ) {
    return { isValid: false, errorKey: STAKE_ERROR_KEYS.INVALID_TYPE };
  }

  if (amount < 0) {
    return { isValid: false, errorKey: STAKE_ERROR_KEYS.NEGATIVE };
  }

  if (amount === 0) {
    return { isValid: true, errorKey: null };
  }

  if (amount < limits.min) {
    return { isValid: false, errorKey: STAKE_ERROR_KEYS.BELOW_MINIMUM };
  }
  if (amount > limits.max) {
    return { isValid: false, errorKey: STAKE_ERROR_KEYS.ABOVE_MAXIMUM };
  }

  return { isValid: true, errorKey: null };
};

// ========== YUP SCHEMA ==========

/**
 * Builds a Yup schema for the given chain limits.
 * Use this from form components: `stakeSchemaFor(limits)`.
 */
export const stakeSchemaFor = (limits: StakeLimits = STAKE_LIMITS) =>
  yup
    .number()
    .typeError('Stake must be a number')
    .required('Stake is required')
    .min(0, 'Stake cannot be negative')
    .test(
      'stake-range',
      `Stake must be 0 (Free) or between ${limits.min} and ${limits.max}`,
      (value) => {
        if (typeof value !== 'number') {
          return false;
        }

        if (value === 0) {
          return true;
        }

        return value >= limits.min && value <= limits.max;
      },
    );

/**
 * Default POL Yup schema, preserved for tests/legacy imports.
 */
export const stakeSchema = stakeSchemaFor();

/**
 * Convenience helper for validating a stake value.
 */
export const validateStake = (stake: unknown) => stakeSchema.validate(stake);
